import React, { useState, useEffect, useRef } from 'react';
import { Grid2 } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import moment from 'moment';
import ExpenseDatePicker from './ExpenseDatePicker';
import { ExpenseStatusValuesEnum } from './ExpenseStatus.enum';
import { COLORS } from '../../../utils/Application_Constants';
import eventBus from './EventBus';
import { ExpenseModeEnum } from './ExpenseMode.enum';

const ExpenseTransactionDate = (props) => {
    const { index, expense, isReimbursement, mode, isPosted, isPendingSubmission, isViewOnly } =
        props;
    const { control, watch, errors, setValue, trigger } = useFormContext();
    const billType = watch(`expenseEntries[${index}].billType`);
    const minDate =
        mode === ExpenseModeEnum.CreditCard
            ? null
            : billType === 'NonBillTeam'
            ? moment(new Date()).startOf('day').subtract(12, 'months')
            : moment(new Date()).startOf('day').subtract(6, 'months');
    const maxDate = moment(new Date()).startOf('day');
    const expenseCategory = watch(`expenseEntries[${index}].category`);
    const expenseType = watch(`expenseEntries[${index}].type`);
    const isMileage =
        expenseType && expenseCategory && expenseCategory === 'Travel' && expenseType === 'Mileage';
    const importedData = watch(`expenseEntries[0].importedData`);
    const [required, setRequired] = useState({ required: true });
    const hasMounted = useRef(false);
    const previousBillTypeRef = useRef(null);
    const handleDateChange = (e) => {
        if (e && e.startOf('day')) {
            setValue(
                `expenseEntries[${index}].transactionDate`,
                e.startOf('day').format('YYYY-MM-DD')
            );
            eventBus.emit(`txnDateChanged${index}`, {
                data: e.startOf('day').format('YYYY-MM-DD'),
            });
        } else {
            setValue(`expenseEntries[${index}].transactionDate`, e);
            eventBus.emit(`txnDateChanged${index}`, { data: e });
        }
        trigger(`expenseEntries[${index}].transactionDate`);
    };

    useEffect(() => {
        const aDate = expense.transactionDate
            ? moment(expense.transactionDate).startOf('day').format('YYYY-MM-DD')
            : null;
        eventBus.emit(`txnDateChanged${index}`, { data: aDate });
    }, []);

    useEffect(() => {
        setRequired({
            required:
                expense?.status !== ExpenseStatusValuesEnum.Abandoned &&
                (mode !== 'cc' || expense?.status === ExpenseStatusValuesEnum.Approved),
        });
    }, [expense?.status]);

    useEffect(() => {
        if (hasMounted.current) {
            const previousBillType = previousBillTypeRef.current;

            // Only trigger validation if both previous and current billType are non-null
            if (previousBillType && billType) {
                trigger(`expenseEntries[${index}].transactionDate`);
            }

            previousBillTypeRef.current = billType;
        } else {
            hasMounted.current = true;
            previousBillTypeRef.current = billType;
        }
    }, [billType]);

    return (
        <Grid2 item size={{ xs: 12, md: 6 }}>
            <Controller
                control={control}
                name={`expenseEntries[${index}].transactionDate`}
                defaultValue={expense.transactionDate || null}
                rules={
                    isPosted
                        ? {}
                        : {
                              required:
                                  required.required && !isMileage
                                      ? 'Transaction date required'
                                      : false,
                              validate: {
                                  invalid: (value) =>
                                      !value || moment(value).isValid() || 'Invalid date',
                                  maxDate: (value) =>
                                      !value ||
                                      moment(value).isSameOrBefore(maxDate) ||
                                      'Max date reached',
                                  minDate: (value) =>
                                      !minDate ||
                                      !value ||
                                      moment(value).isSameOrAfter(minDate) ||
                                      'Min date reached',
                              },
                          }
                }
                render={({ onChange, value }) => {
                    const isTransactionDateInImportedData =
                        importedData && 'Transaction Date' in importedData;

                    return (
                        <ExpenseDatePicker
                            onChange={(e) => {
                                onChange(e);
                                handleDateChange(e);
                            }}
                            value={value ? moment(value) : null}
                            id={`transaction-date${index}`}
                            label="Transaction date"
                            disabled={
                                isViewOnly ||
                                (isTransactionDateInImportedData &&
                                    !isReimbursement &&
                                    mode !== ExpenseModeEnum.CreditCard) ||
                                (isPosted && isViewOnly) ||
                                isPendingSubmission ||
                                !billType
                            }
                            required={required.required && !isMileage}
                            minDate={minDate}
                            maxDate={maxDate}
                            parentTextFieldProps={{
                                error: Boolean(errors.expenseEntries?.[index]?.transactionDate),
                                helperText:
                                    errors.expenseEntries?.[index]?.transactionDate?.message,
                            }}
                            parentSx={{
                                '& .MuiInputBase-root': {
                                    borderColor: !errors.expenseEntries?.[index]?.transactionDate
                                        ? 'default'
                                        : `${COLORS.LT_PEPPER} !important`,
                                },
                            }}
                        />
                    );
                }}
            />
        </Grid2>
    );
};

export default ExpenseTransactionDate;
